/**
 * Kimlik dogrulamali fetch - CSRF token'ini OTOMATIK ekliyor.
 *
 * Merkezi olmasi onemli: her cagri yerine tek noktada yapiliyor, yani bir
 * rotayi CSRF header'i olmadan cagirmak MUMKUN olmuyor. Vanilla arayuzde de
 * ayni yaklasim kullanildi ve sebebi ayni.
 *
 * Oturum token'ina JS HIC dokunmuyor: `HttpOnly` cookie'de. Enjekte edilmis
 * bir script tasinabilir uzun omurlu bir credential'i disari CIKARAMIYOR.
 * CSRF token'i ise okunabilir bir cookie'de ve her mutasyonda header'a
 * kopyalaniyor.
 */

export interface SessionState {
  authenticated: boolean
  mfa_pending: boolean
  user?: {
    id: string
    email: string
    name: string
    role: string
    status: string
    totp_enabled: boolean
    can_run_code: boolean
    /** Kayitli arayuz dili - boot'ta uygulaniyor (bkz. `applyServerLocale`). */
    locale?: string
    /** Kayitli tema tercihi - `null`/yok = cihazi takip et (bkz. `ThemeProvider`). */
    theme?: string | null
    /** Ayarlanmadiysa `null` - header/menu baş harf daireye duser. */
    avatar_attachment_id?: string | null
  }
  csrf_token?: string
  session_expires_at?: string
  registration_mode: string
  google_enabled: boolean
}

let csrfToken: string | null = null

export function setCsrfToken(token: string | null): void {
  csrfToken = token
}

/** `uploadFile()` gibi `fetch` sarmalayicisi (`api()`) KULLANMAYAN cagiranlar icin. */
export function getCsrfToken(): string | null {
  return csrfToken
}

export class ApiError extends Error {
  readonly status: number
  /** Sunucunun `Retry-After` basligi (429/503). */
  readonly retryAfter?: number
  /**
   * Sunucunun KABA hata kodu (`invalid_credentials`, `run_in_progress` …).
   *
   * `message` her zaman Turkce geliyor - sunucu sablonlari cevrilmiyor.
   * Coklu dil arayuzunde bu, Ingilizce bir ekranda Turkce hata gostermek
   * demek olurdu. `code` bunu cozuyor: istemci once `error.<code>`
   * anahtarini deniyor, bulamazsa sunucu mesajina dusuyor. Bu yuzden kodun
   * govdeden CIKARILMASI sart - onceki hali yalnizca mesaji okuyordu ve
   * cevirinin tutunacagi hicbir yer yoktu.
   */
  readonly code?: string

  // Alanlar govdede atanıyor, constructor parametre ozelligi olarak DEGIL:
  // `erasableSyntaxOnly` acik (tsconfig) ve parametre ozellikleri calisma
  // zamani kodu uretiyor, yani "yalnizca tip silinerek derlenebilir" kuralini
  // ihlal ediyor.
  constructor(status: number, message: string, retryAfter?: number, code?: string) {
    super(message)
    this.status = status
    this.retryAfter = retryAfter
    this.code = code
  }
}

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS'])

export async function api(path: string, options: RequestInit = {}): Promise<Response> {
  const method = (options.method ?? 'GET').toUpperCase()
  const headers = new Headers(options.headers)
  if (!SAFE_METHODS.has(method) && csrfToken) {
    headers.set('X-CSRF-Token', csrfToken)
  }
  // Yalnizca DIZE govdeler (JSON.stringify sonucu) icin varsayilan
  // `application/json`. `FormData` govdeler (avatar/sesli mesaj yuklemeleri
  // - `profile.ts`/`MicButton.tsx`) buraya BILEREK Content-Type VERMIYOR:
  // tarayicinin kendi multipart boundary'sini yazmasi gerekiyor. Onceden bu
  // kontrol `options.body` varligina bakiyordu ve FormData govdeleri de
  // zorla `application/json`a ceviriyordu - sunucu govdeyi JSON gibi
  // ayristirmaya calisip `file` alanini asla BULAMIYORDU (422 "missing").
  if (typeof options.body === 'string' && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }
  return fetch(path, { ...options, headers, credentials: 'same-origin' })
}

/**
 * 401 gorulunce cagrilacak kanca.
 *
 * `SessionProvider` bunu kaydediyor ve yeniden dogrulama modal'ini aciyor.
 * Merkezi olmasi onemli: her cagri yerinde 401 kontrolu yapmak, bir tanesini
 * atlamak ve orada sessizce bos veri gostermek demek olurdu.
 *
 * DIKKAT: auth rotalarinin KENDISI 401 dondurebiliyor (yanlis sifre) ve o
 * bir oturum kaybi DEGIL. `isAuthRoute` bu ayrimi yapiyor.
 */
let onUnauthorized: (() => void) | null = null

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler
}

/** Auth rotalarinda 401 normal bir sonuc, oturum kaybi degil. */
function isAuthRoute(path: string): boolean {
  return path.startsWith('/api/auth/')
}

/**
 * 401'in "sifre hatali" demek oldugu uclar.
 *
 * Hassas islemler (TOTP kurma/kapatma, yedek kod yenileme, e-posta
 * degistirme, hesap silme, API anahtari olusturma) oturumun yaninda bir de
 * SIFRE istiyor ve backend yanlis sifreye 401 donuyor (bkz. me_routes.py
 * `_require_password`). Bu 401 oturumun bittigi anlamina GELMIYOR - ama
 * varsayilan kanca onu oyle sayip "yeniden giris yapin" modal'ini
 * aciyordu: kullanici bir harfi yanlis yazdi diye butun ekranin uzerine
 * kapatilamayan bir pencere geliyordu. Formun kendi hata satiri zaten
 * dogru mesaji gosteriyor.
 *
 * Metot da onemli: `GET /api/me` 401 donerse bu GERCEKTEN oturum kaybi,
 * `DELETE /api/me` 401 donerse sifre hatasi. Yol tek basina ayirt etmiyor.
 */
const PASSWORD_CONFIRMED: readonly [string, string][] = [
  ['POST', '/api/me/totp/enroll'],
  ['POST', '/api/me/totp/disable/request'],
  ['POST', '/api/me/totp/disable/confirm'],
  ['POST', '/api/me/totp/backup-codes'],
  ['POST', '/api/me/email'],
  ['POST', '/api/me/email/confirm'],
  ['POST', '/api/me/api-keys'],
  ['DELETE', '/api/me'],
]

function isPasswordConfirmRoute(path: string, method: string): boolean {
  const verb = method.toUpperCase()
  // Sorgu dizesi olmadan karsilastiriliyor; bu uclarin hicbiri almiyor.
  const clean = path.split('?')[0]
  return PASSWORD_CONFIRMED.some(([m, p]) => m === verb && p === clean)
}

function noteStatus(path: string, method: string, status: number): void {
  if (status !== 401) return
  if (isAuthRoute(path) || isPasswordConfirmRoute(path, method)) return
  notifyUnauthorized()
}

/**
 * Oturum kaybini ACIKCA bildirir.
 *
 * `apiJson`/`apiVoid` disindaki cagiranlar icin: SSE akisi ham `fetch`
 * kullaniyor (govdeyi kendisi okumasi gerekiyor) ve o yuzden otomatik
 * kancaya takilmiyor. Bu boslugu kapatmadan uzun bir akis sirasinda sona
 * eren oturum, kullaniciya kurtarma yolu OLMAYAN bir hata mesaji gosteriyor -
 * planin "oturum bitince modal, sonra `after=lastSeq` ile kayipsiz resume"
 * akisi hic devreye girmiyordu.
 */
export function notifyUnauthorized(): void {
  onUnauthorized?.()
}

/** JSON bekleyen cagri. Hata durumunda `ApiError` firlatiyor. */
export async function apiJson<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await api(path, options)
  if (!response.ok) {
    noteStatus(path, options.method ?? 'GET', response.status)
    const detail = await readErrorDetail(response)
    throw new ApiError(response.status, detail.message, readRetryAfter(response), detail.code)
  }
  return (await response.json()) as T
}

/**
 * Govde DONDURMEYEN cagri (204).
 *
 * `apiJson` kullanilamaz: bos govdede `response.json()` `SyntaxError`
 * firlatiyor ve o hatayi yakalayip yutmak, GERCEK bir bozuk-JSON hatasini da
 * yutmak demek olurdu. Ayri bir fonksiyon ayrimi net tutuyor.
 */
export async function apiVoid(path: string, options: RequestInit = {}): Promise<void> {
  const response = await api(path, options)
  if (!response.ok) {
    noteStatus(path, options.method ?? 'GET', response.status)
    const detail = await readErrorDetail(response)
    throw new ApiError(response.status, detail.message, readRetryAfter(response), detail.code)
  }
}

function readRetryAfter(response: Response): number | undefined {
  const raw = response.headers.get('Retry-After')
  if (!raw) return undefined
  const parsed = Number.parseInt(raw, 10)
  return Number.isFinite(parsed) ? parsed : undefined
}

/**
 * Sunucu hata mesajini cikarir.
 *
 * Sunucu ham exception metni GONDERMIYOR (kaba kod + Turkce mesaj); burada
 * yalnizca onu okuyoruz. Govde okunamazsa status'e dusuyoruz - tek bir
 * bozuk yanit yuzunden "undefined" gostermek kotu olurdu.
 */
export async function readError(response: Response): Promise<string> {
  return (await readErrorDetail(response)).message
}

/**
 * Hata govdesinden mesaj VE kodu birlikte cikarir.
 *
 * Govde tek sefer okunabilir (`response.json()` akisi tuketiyor), o yuzden
 * ikisini ayri fonksiyonlarda okumak MUMKUN degil - bu, `readError`in
 * artik bunun uzerine oturmasinin sebebi.
 */
export async function readErrorDetail(
  response: Response,
): Promise<{ message: string; code?: string }> {
  try {
    const body = await response.json()
    return {
      message: body?.detail ?? body?.error?.message ?? `Hata ${response.status}`,
      code: typeof body?.error?.code === 'string' ? body.error.code : undefined,
    }
  } catch {
    return { message: `Hata ${response.status}` }
  }
}

export async function loadSession(): Promise<SessionState | null> {
  try {
    const response = await fetch('/api/auth/session', { credentials: 'same-origin' })
    if (!response.ok) return null
    const state = (await response.json()) as SessionState
    setCsrfToken(state.csrf_token ?? null)
    return state
  } catch {
    return null
  }
}
